import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { useSurfaceSample } from '../surfaces/util/useSurfaceSample'
import React from 'react'
import { Group, Object3D, Vector3 } from 'three'
import { GeckoComponentCoords, GeckoFeatureModelType } from 'types'

const vec3One: Readonly<Vector3> = new Vector3(1, 1, 1)

/**
 * A component used to position and align a feature on the surface of a component
 * @param model             The manway model (from the asset model)
 * @param children          The 3D objects making up this feature
 * @constructor
 */
export const SurfaceFeature3D: React.FC<{
  model: {
    id: string
    type: GeckoFeatureModelType
    geometry: GeckoComponentCoords
  }
  children: React.ReactNode
}> = ({ model, children }) => {
  //----------------------------------------------------------------------------

  const ref = React.useRef<Group>(null)
  const { show3D } = useAssetVizInternal()
  const { position, normal } = useSurfaceSample(model.geometry)

  React.useLayoutEffect(() => {
    if (!ref.current) return
    if (!ref.current.parent || !position || !normal) {
      ref.current.visible = false
      return
    }

    const obj = new Object3D()
    obj.lookAt(normal)

    ref.current.visible = true
    ref.current.position.set(0, 0, 0)
    ref.current.setRotationFromQuaternion(obj.quaternion)
    ref.current.rotateX(Math.PI / 2.0)
    ref.current.position.copy(position)

    const scale = ref.current.parent.getWorldScale(vec3One)
    ref.current.scale.set(
      1.0 / scale.x,
      (show3D ? 1.0 : 0.01) / scale.z, // Squash in 2D mode
      1.0 / scale.y
    )
  }, [normal, position, show3D])

  return !position || !normal ? null : (
    <group
      ref={ref}
      key={
        // Forces a re-render when changing from 2D to 3D
        show3D ? '3D' : '2D'
      }
      name={`${model.type}_${model.id}`}>
      {children}
    </group>
  )
}
